import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, MapPin, Search, X } from "lucide-react";
import colombia from "@/data/colombia.json";

type DepartamentoJSON = {
  id: number;
  departamento: string;
  ciudades: string[];
};

interface CityFilterProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
}

export function CityFilter({
  value,
  onChange,
  label,
  placeholder = "Todas las ciudades",
}: CityFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return colombia as DepartamentoJSON[];
    return (colombia as DepartamentoJSON[])
      .map((d) => ({
        ...d,
        ciudades: d.ciudades.filter((c) => c.toLowerCase().includes(q)),
      }))
      .filter((d) => d.ciudades.length > 0 || d.departamento.toLowerCase().includes(q));
  }, [query]);

  const select = (city: string, dept: string) => {
    onChange(`${dept} - ${city}`);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {label && (
        <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 6 }}>
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderRadius: 12,
          border: "1px solid rgba(148, 163, 184, 0.25)",
          background: "rgba(15, 23, 42, 0.7)",
          color: value ? "#e2e8f0" : "#94a3b8",
          fontSize: 13,
          textAlign: "left",
          cursor: "pointer",
          backdropFilter: "blur(8px)",
        }}
      >
        <MapPin size={16} color="#93c5fd" style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        {value ? (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            style={{ display: "inline-flex", cursor: "pointer" }}
          >
            <X size={15} color="#94a3b8" />
          </span>
        ) : (
          <ChevronDown size={15} color="#94a3b8" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "absolute",
              zIndex: 60,
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              background: "#0f172a",
              border: "1px solid rgba(148, 163, 184, 0.2)",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
              <Search size={15} color="#64748b" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar ciudad..."
                style={{
                  flex: 1,
                  background: "none",
                  border: "none",
                  outline: "none",
                  color: "#e2e8f0",
                  fontSize: 13,
                }}
              />
            </div>
            <div style={{ maxHeight: 260, overflowY: "auto", padding: 6 }}>
              {filtered.length === 0 && (
                <p style={{ padding: "16px 8px", textAlign: "center", color: "#64748b", fontSize: 13 }}>
                  No se encontraron ciudades
                </p>
              )}
              {filtered.map((dept) => (
                <div key={dept.id}>
                  <p style={{ padding: "4px 10px", margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b" }}>
                    {dept.departamento}
                  </p>
                  {dept.ciudades.map((city) => {
                    const full = `${dept.departamento} - ${city}`;
                    const selected = value === full;
                    return (
                      <button
                        key={`${dept.id}-${city}`}
                        type="button"
                        onClick={() => select(city, dept.departamento)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "7px 10px",
                          borderRadius: 8,
                          background: selected ? "rgba(59, 130, 246, 0.12)" : "none",
                          color: selected ? "#93c5fd" : "#cbd5e1",
                          fontSize: 13,
                          textAlign: "left",
                          cursor: "pointer",
                          border: "none",
                        }}
                      >
                        <span style={{ flex: 1 }}>{city}</span>
                        {selected && <CheckDot />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const CheckDot = () => (
  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#3b82f6", flexShrink: 0 }} />
);
